import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function NotificationsPage({ user, onLogout }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [showUnreadOnly]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/notifications/my-notifications?unread_only=${showUnreadOnly}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/notifications/${notificationId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (unreadCount === 0) {
      alert('No unread notifications');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('All notifications marked as read!');
      fetchNotifications();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchNotifications();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'leave_approved': return '✅';
      case 'leave_rejected': return '❌';
      case 'payroll_generated': return '💰';
      case 'attendance_reminder': return '🕔';
      case 'birthday': return '🎉';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'leave_approved': return 'bg-green-50 border-green-200';
      case 'leave_rejected': return 'bg-red-50 border-red-200';
      case 'payroll_generated': return 'bg-blue-50 border-blue-200';
      case 'attendance_reminder': return 'bg-yellow-50 border-yellow-200';
      case 'birthday': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
            <p className="mt-2 text-gray-600">
              {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={markAllAsRead}
              disabled={loading || unreadCount === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-400"
            >
              Mark All Read
            </button>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="mb-6">
          <button
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            className={`px-6 py-2 rounded-lg transition ${
              showUnreadOnly
                ? 'bg-[#2b3380] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showUnreadOnly ? '🔔 Show Unread Only' : '📨 Show All'}
          </button>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              <div className="text-6xl mb-4">📨</div>
              <p className="text-lg">No notifications found</p>
              <p className="text-sm mt-2">We'll notify you when something important happens</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-lg shadow-md p-5 border-2 ${
                  notification.is_read ? 'bg-white border-gray-200' : getNotificationColor(notification.type)
                } transition hover:shadow-lg`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className="text-3xl mr-4">{getNotificationIcon(notification.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{notification.title}</h3>
                        {!notification.is_read && (
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">New</span>
                        )}
                      </div>
                      <p className="text-gray-700 mb-2">{notification.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(notification.created_at).toLocaleString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {!notification.is_read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info Card */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">💡 About Notifications</h3>
          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
            <li>Get real-time updates on leave approvals, payroll, and more</li>
            <li>Important notifications are also sent via email</li>
            <li>You can customize notification preferences in your profile settings</li>
            <li>Notifications are automatically marked as read when you view them</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}

export default NotificationsPage;